import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdEdit, MdChevronLeft, MdChevronRight, MdSwapHoriz, MdLogin, MdSensors, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { userDisplayName } from 'utils/userName';

interface User {
    _id: string;
    name?: string;
    surname?: string;
    email: string;
    type: 'buyer' | 'seller';
    role?: 'admin';
    website?: string;
    createdAt: string;
    alreadyPaidAmount?: number;
    // Acquisition channels assigned to a seller (see Back Office > Sellers Channels).
    channels?: string[];
    // Present only for sellers (see GET /admin/users). Each flag is a seller-onboarding
    // checkpoint surfaced as a checkbox in the list.
    setupStatus?: {
        iban: boolean;
        stripe: boolean;
        phone: boolean;
        website: boolean;
        pixel: boolean;
        iframe: boolean;
    };
}

const SETUP_ITEMS: { key: keyof NonNullable<User['setupStatus']>; label: string }[] = [
    { key: 'iban', label: 'IBAN' },
    { key: 'stripe', label: 'Stripe' },
    { key: 'phone', label: 'Tél' },
    { key: 'website', label: 'Web' },
    { key: 'pixel', label: 'Pixel' },
    { key: 'iframe', label: 'Iframe' },
];

type SetupKey = keyof NonNullable<User['setupStatus']>;
type SetupFilters = Record<SetupKey, boolean>;
const EMPTY_SETUP_FILTERS: SetupFilters = { iban: false, stripe: false, phone: false, website: false, pixel: false, iframe: false };

const TYPE_TABS: { value: string; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'seller', label: 'Sellers' },
    { value: 'buyer', label: 'Buyers' },
    { value: 'admin', label: 'Admins' },
];

function ChannelPills({ channels }: { channels?: string[] }) {
    if (!channels || channels.length === 0) {
        return <span className="text-xs text-amber-600 dark:text-amber-400">Aucun canal</span>;
    }
    return (
        <div className="flex max-w-[220px] flex-wrap gap-1">
            {channels.map((channel) => (
                <span
                    key={channel}
                    className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-navy-700 dark:text-gray-300"
                >
                    {channel}
                </span>
            ))}
        </div>
    );
}

function SetupChecklist({ status }: { status: User['setupStatus'] }) {
    if (!status) return <span className="text-gray-300">—</span>;
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {SETUP_ITEMS.map(({ key, label }) => {
                const done = status[key];
                return (
                    <span
                        key={key}
                        title={`${label}${done ? ' : OK' : ' : manquant'}`}
                        className={`inline-flex items-center gap-1 text-xs ${
                            done ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
                        }`}
                    >
                        {done ? <MdCheckBox size={16} /> : <MdCheckBoxOutlineBlank size={16} />}
                        {label}
                    </span>
                );
            })}
        </div>
    );
}

export default function BackOfficeUsers() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [filterType, setFilterType] = useState('all');
    const [setupFilters, setSetupFilters] = useState<SetupFilters>(EMPTY_SETUP_FILTERS);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [limit] = useState(20);
    const [switchingUserId, setSwitchingUserId] = useState<string | null>(null);
    const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
    const [sellersMissingChannels, setSellersMissingChannels] = useState<number | null>(null);

    const setupFilterKey = JSON.stringify(setupFilters);

    useEffect(() => {
        fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm, filterType, setupFilterKey]);

    useEffect(() => {
        fetchSellersMissingChannels();
    }, []);

    const fetchSellersMissingChannels = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/sellers/missing-channels`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSellersMissingChannels(response.data.total);
        } catch (error) {
            console.error('Error fetching sellers missing channels:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = {
                page: currentPage,
                limit: limit
            };
            
            if (searchTerm) {
                params.search = searchTerm;
            }
            
            if (filterType && filterType !== 'all') {
                params.type = filterType;
            }

            (Object.keys(setupFilters) as SetupKey[]).forEach((key) => {
                if (setupFilters[key]) params[key] = 'true';
            });

            const response = await axios.get(`${CONFIG.apiUrl}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            setUsers(response.data.users);
            setTotalPages(response.data.pages);
            setTotalUsers(response.data.total);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1); // Reset to first page on search
    };

    const handleFilterChange = (value: string) => {
        setFilterType(value);
        setCurrentPage(1); // Reset to first page on filter change
        // Setup filters only make sense for sellers (the API forces a seller scope when any
        // is active), so drop them when moving to a buyer/admin-only tab.
        if (value === 'buyer' || value === 'admin') {
            setSetupFilters(EMPTY_SETUP_FILTERS);
        }
    };

    const toggleSetupFilter = (key: SetupKey) => {
        setSetupFilters((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            // A setup filter implies sellers — keep the visible tab in sync.
            if (next[key] && filterType !== 'seller') setFilterType('seller');
            return next;
        });
        setCurrentPage(1);
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const impersonateUser = async (user: User) => {
        try {
            setImpersonatingUserId(user._id);
            const token = localStorage.getItem('token');
            await axios.get(`${CONFIG.apiUrl}/auth/impersonate/${encodeURIComponent(user.email)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(`Impersonation link sent to Slack for ${user.email}`);
        } catch (error) {
            console.error('Error impersonating user:', error);
            alert('Failed to impersonate user');
        } finally {
            setImpersonatingUserId(null);
        }
    };

    const switchUserType = async (user: User) => {
        const newType = user.type === 'buyer' ? 'seller' : 'buyer';
        try {
            setSwitchingUserId(user._id);
            const token = localStorage.getItem('token');
            await axios.patch(`${CONFIG.apiUrl}/admin/users/${user._id}`, { type: newType }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(prev => prev.map(u => u._id === user._id ? { ...u, type: newType } : u));
        } catch (error) {
            console.error('Error switching user type:', error);
        } finally {
            setSwitchingUserId(null);
        }
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Users Management
                    </div>
                    <button
                        onClick={() => navigate('/back-office/sellers-channels')}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                        <MdSensors />
                        Sellers Channels
                        {!!sellersMissingChannels && (
                            <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-bold text-brand-600">
                                {sellersMissingChannels}
                            </span>
                        )}
                    </button>
                </div>

                <div className="mb-4 flex gap-2">
                    <div className="relative flex-1">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MdSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name, company, email, phone, website, SIRET, id..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        />
                    </div>
                </div>

                {/* Type tabs */}
                <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200 dark:border-navy-600">
                    {TYPE_TABS.map((tab) => (
                        <button
                            key={tab.value}
                            onClick={() => handleFilterChange(tab.value)}
                            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                                filterType === tab.value
                                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                                    : 'border-transparent text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Setup checklist filters (sellers) */}
                <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Setup
                    </span>
                    {SETUP_ITEMS.map(({ key, label }) => (
                        <label
                            key={key}
                            className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300"
                        >
                            <input
                                type="checkbox"
                                checked={setupFilters[key]}
                                onChange={() => toggleSetupFilter(key)}
                                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800"
                            />
                            {label}
                        </label>
                    ))}
                    {Object.values(setupFilters).some(Boolean) && (
                        <button
                            onClick={() => { setSetupFilters(EMPTY_SETUP_FILTERS); setCurrentPage(1); }}
                            className="text-xs text-gray-500 underline hover:text-navy-700 dark:hover:text-white"
                        >
                            Clear
                        </button>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Name
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Email
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Website
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Type
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Déjà payé
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Setup
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Channels
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Created At
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="py-4 text-center">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr
                                        key={user._id}
                                        className="border-b border-gray-100 dark:border-navy-700"
                                    >
                                        <td className="py-3 text-sm">
                                            {userDisplayName(user)}
                                        </td>
                                        <td className="py-3 text-sm">{user.email}</td>
                                        <td className="py-3 text-sm">
                                            {user.website ? (
                                                <a
                                                    href={/^https?:\/\//i.test(user.website) ? user.website : `https://${user.website}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-brand-500 hover:underline"
                                                >
                                                    {user.website.replace(/^https?:\/\//i, '')}
                                                </a>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                                    user.type === 'buyer'
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                }`}
                                            >
                                                {user.type}
                                            </span>
                                        </td>
                                        <td className="py-3 text-sm">
                                            {user.type === 'seller' && (user.alreadyPaidAmount ?? 0) > 0 ? (
                                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                    €{(user.alreadyPaidAmount!).toFixed(2)}
                                                </span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            {user.type === 'seller' ? (
                                                <SetupChecklist status={user.setupStatus} />
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            {user.type === 'seller' ? (
                                                <ChannelPills channels={user.channels} />
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/back-office/users/edit/${user._id}`)}
                                                    className="flex items-center gap-1 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-navy-700"
                                                >
                                                    <MdEdit />
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => impersonateUser(user)}
                                                    disabled={impersonatingUserId === user._id}
                                                    title={`Impersonate ${user.email}`}
                                                    className="flex items-center gap-1 rounded px-2 py-1 text-sm text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 dark:text-indigo-400 dark:hover:bg-navy-700"
                                                >
                                                    <MdLogin />
                                                    Impersonate
                                                </button>
                                                {user.type !== undefined && (
                                                    <button
                                                        onClick={() => switchUserType(user)}
                                                        disabled={switchingUserId === user._id}
                                                        title={`Switch to ${user.type === 'buyer' ? 'seller' : 'buyer'}`}
                                                        className="flex items-center gap-1 rounded px-2 py-1 text-sm text-orange-600 hover:bg-orange-50 disabled:opacity-50 dark:text-orange-400 dark:hover:bg-navy-700"
                                                    >
                                                        <MdSwapHoriz />
                                                        → {user.type === 'buyer' ? 'Seller' : 'Buyer'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-navy-600">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Showing page {currentPage} of {totalPages} ({totalUsers} total users)
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft />
                                Previous
                            </button>
                            
                            <div className="flex items-center gap-1">
                                {[...Array(totalPages)].map((_, index) => {
                                    const pageNumber = index + 1;
                                    // Show first page, last page, current page, and pages around current
                                    if (
                                        pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNumber}
                                                onClick={() => goToPage(pageNumber)}
                                                className={`min-w-[2.5rem] rounded-lg px-3 py-2 text-sm font-medium ${
                                                    currentPage === pageNumber
                                                        ? 'bg-brand-500 text-white'
                                                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700'
                                                }`}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    } else if (
                                        pageNumber === currentPage - 2 ||
                                        pageNumber === currentPage + 2
                                    ) {
                                        return (
                                            <span key={pageNumber} className="px-2 text-gray-500">
                                                ...
                                            </span>
                                        );
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                            >
                                Next
                                <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
